import _flexbox from "../dict/flexbox.js";
import _quillClasses from "../dict/quillClasses.js";
import _convertToHtml from "../functions/quill/convertToHtml.js";
import _Event from "../classes/event.js";
import _getAppName from "../functions/helpers/getAppName.js";
import _sccFunc from "../functions/profiles/scenerist.js";
import _ttmlFunc from "../functions/profiles/ttmlGeneral.js";
import { XMLParser as _XMLParser } from "fast-xml-parser";
import { decode as _decodeHtml } from "html-entities";
import _eol from "eol";
import _convertToPlainTextCustom from "../functions/quill/convertToPlainTextCustom.js";
import _convertToPlainText from "../functions/quill/convertToPlainText.js";
import _getFormatOptions from "../functions/helpers/getFormatOptions.js";
import _removeInvalidEvents from "../functions/eventGroups/removeInvalidEvents.js";
import _tcLib from "../lib/timecode.js";
const tcLib = _tcLib;
const removeInvalidEvents = _removeInvalidEvents;
const getFormatOptions = _getFormatOptions;
const convertToPlainText = _convertToPlainText;
const convertToPlainTextCustom = _convertToPlainTextCustom;
const eol = _eol;
const decodeHtml = _decodeHtml;
const XMLParser = _XMLParser; //Decode
const ttmlFunc = _ttmlFunc;
const sccFunc = _sccFunc;
const getAppName = _getAppName;
const Event = _Event;
const convertToHtml = _convertToHtml;
const quillClasses = _quillClasses;
const flexbox = _flexbox;

/* Premiere Pro exports each caption line as its own <p> positioned on the CEA-608 grid (32 columns x 15 rows inside a 10% safe area). Each column is 2.5% wide (80/32) and each row is 5.333% tall (80/15). */
const ADOBE_GRID = {
  padding: 10,
  columnWidth: 80 / 32,
  rowHeight: 80 / 15,
  columns: 32,
  rows: 15
};
const parsePercentPair = function (value) {
  if (!value) {
    return null;
  }
  let parts = value.toString().trim().split(/\s+/).map(part => parseFloat(part.replace("%", "")));
  if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) {
    return null;
  }
  return {
    x: parts[0],
    y: parts[1]
  };
};
const originToGrid = function (origin) {
  let col = Math.round((origin.x - ADOBE_GRID.padding) / ADOBE_GRID.columnWidth);
  let row = Math.round((origin.y - ADOBE_GRID.padding) / ADOBE_GRID.rowHeight);
  return {
    col: Math.min(ADOBE_GRID.columns - 1, Math.max(0, col)),
    row: Math.min(ADOBE_GRID.rows - 1, Math.max(0, row))
  };
};

/* Premiere puts tts:backgroundColor (#000000FF), tts:fontFamily and tts:fontSize on every span. Drop the defaults so they don't decode as explicit formatting (e.g. <bgBlack>), and drop the indentation between sibling spans. */
const normalizeAdobeSpans = function (text) {
  return text.replace(/<span\b([^>]*)>/gi, (match, attrs) => {
    let cleaned = attrs.replace(/\s+tts:(fontFamily|fontSize)\s*=\s*(['"])[^'"]*\2/gi, "").replace(/\s+tts:backgroundColor\s*=\s*(['"])(#000000(ff)?|black)\1/gi, "").replace(/\s+tts:fontStyle\s*=\s*(['"])normal\1/gi, "");
    return `<span${cleaned}>`;
  }).replace(/<\/span>\s+<span/gi, "</span><span").trim();
};

/* Premiere always writes real-time 24fps timecode (ttp:frameRate="24", nonDrop, no multiplier) regardless of the sequence rate, so frames must be counted at the file's rate rather than the job's. */
const ntscRateMap = {
  "24": 23.976,
  "30": 29.97,
  "60": 59.94
};
const getTimingInfo = function (input, options) {
  let frameRateMatch = input.match(/ttp:frameRate\s*=\s*["'](\d+)["']/);
  if (!frameRateMatch) {
    return {
      frameRate: options.frameRate,
      dropFrame: options.dropFrame
    };
  }
  let baseRate = frameRateMatch[1];
  let multiplierMatch = input.match(/ttp:frameRateMultiplier\s*=\s*["']([^"']+)["']/);
  let dropModeMatch = input.match(/ttp:dropMode\s*=\s*["']([^"']+)["']/);
  let isNtsc = multiplierMatch && multiplierMatch[1].trim() === "1000 1001";
  let frameRate = isNtsc && ntscRateMap[baseRate] ? ntscRateMap[baseRate] : parseFloat(baseRate);
  let dropFrame = dropModeMatch ? /^drop/i.test(dropModeMatch[1]) : options.dropFrame;
  return {
    frameRate: frameRate,
    dropFrame: dropFrame && (frameRate === 29.97 || frameRate === 59.94)
  };
};
const getParagraphs = function (fileJson) {
  let paragraphs = [];
  let divs = fileJson.tt.body.div;
  divs = Array.isArray(divs) ? divs : [divs];
  divs.filter(Boolean).forEach(div => {
    let divParagraphs = Array.isArray(div.p) ? div.p : [div.p];
    divParagraphs.filter(Boolean).forEach(paragraph => {
      paragraphs.push({
        paragraph: paragraph,
        regionId: paragraph["@_region"] || div["@_region"]
      });
    });
  });
  return paragraphs;
};

/* Files written by our own adobeTtml encoder carry tts:extent; decode them with the generic single-line TTML path which mirrors singleLine.calcOrigin/calcExtents. */
const decodeWithExtents = function (paragraphs, regions, timing, useClockTime, options) {
  let displays = [];
  paragraphs.forEach(({
    paragraph,
    regionId
  }) => {
    let start = tcLib.parseTcToSec(paragraph["@_begin"], timing.frameRate, timing.dropFrame, useClockTime);
    let end = tcLib.parseTcToSec(paragraph["@_end"], timing.frameRate, timing.dropFrame, useClockTime);
    let original = decodeHtml((paragraph["#text"] || "").toString());
    let style = ttmlFunc.getStyleFromRegionId(regionId);
    let origin = parsePercentPair(paragraph["@_tts:origin"]) || {};
    let extent = parsePercentPair(paragraph["@_tts:extent"]) || {};
    let line = {
      text: convertToPlainText(original),
      html: ttmlFunc.multiLine.formatText(original),
      original: original,
      extentX: extent.x,
      extentY: extent.y,
      originX: origin.x,
      originY: origin.y,
      region: regions[regionId] || {}
    };
    let lastDisplay = displays[displays.length - 1];
    if (lastDisplay && lastDisplay.start === start) {
      lastDisplay.insertLine(line);
    } else {
      let ccDisplay = new ttmlFunc.singleLine.Display();
      ccDisplay.start = start;
      ccDisplay.end = end;
      ccDisplay.style = style;
      ccDisplay.insertLine(line);
      displays.push(ccDisplay);
    }
  });
  return displays.map(display => {
    let event = ttmlFunc.singleLine.decodeDisplay(display, options.window);
    event.style = display.style;
    return event;
  });
};

/* Premiere Pro exports: one <p> per line, tts:origin on the 608 grid, no tts:extent, and tts:textAlign when the line isn't left aligned. */
const decodePremiere = function (paragraphs, styles, timing, useClockTime, options) {
  let displays = [];
  paragraphs.forEach(({
    paragraph,
    regionId
  }) => {
    let begin = paragraph["@_begin"],
      end = paragraph["@_end"];
    let html = ttmlFunc.multiLine.formatText(normalizeAdobeSpans((paragraph["#text"] || "").toString()));
    let text = convertToPlainText(html).trim();
    if (!text) {
      return;
    }
    let origin = parsePercentPair(paragraph["@_tts:origin"]) || {
      x: ADOBE_GRID.padding,
      y: ADOBE_GRID.padding + (ADOBE_GRID.rows - 1) * ADOBE_GRID.rowHeight
    };
    let grid = originToGrid(origin);
    let styleInfo = styles[paragraph["@_style"]] || {};
    let line = {
      text: text,
      html: html,
      xPos: grid.col,
      yPos: grid.row,
      textAlign: paragraph["@_tts:textAlign"] || styleInfo["@_tts:textAlign"]
    };
    let lastDisplay = displays[displays.length - 1];
    if (lastDisplay && lastDisplay.begin === begin && lastDisplay.end === end) {
      lastDisplay.lines.push(line);
    } else {
      displays.push({
        begin: begin,
        end: end,
        style: ttmlFunc.getStyleFromRegionId(regionId),
        lines: [line]
      });
    }
  });
  return displays.map(display => {
    let lines = display.lines.sort((lineA, lineB) => lineA.yPos - lineB.yPos || lineA.xPos - lineB.xPos);
    let position = sccFunc.getPositionFromDisplay(JSON.parse(JSON.stringify(lines)), options.window, display.style);
    let explicitAlign = lines.find(line => line.textAlign)?.textAlign;
    let alignment = flexbox.alignmentNormalize[explicitAlign] || position.alignment;
    let ccEvent = new Event({
      style: display.style,
      start: tcLib.parseTcToSec(display.begin, timing.frameRate, timing.dropFrame, useClockTime),
      end: tcLib.parseTcToSec(display.end, timing.frameRate, timing.dropFrame, useClockTime)
    });
    ccEvent.alignment = alignment;
    ccEvent.xPos = position.xPos;
    ccEvent.yPos = position.yPos;
    ccEvent.text = convertToHtml(lines.map(line => line.html).join("\n"), [quillClasses.align[alignment]]);
    return ccEvent;
  });
};
const getRegion = function (index, style) {
  let region = ttmlFunc.eventStyleMap[style];
  if (region === "pop") {
    return region + (index + 1);
  } else if (region === "paint") {
    if (index === 0) {
      return region;
    } else {
      return region + (index + 1);
    }
  } else {
    return region + (index + 2);
  }
};

/* Premiere workaround: set tts:backgroundColor on every span so italics don't drop the region bg.
   Prefer a span's CSS background-color when present; otherwise use the format-option default. */
const applySpanBackgroundColor = function (ttmlText, defaultBackgroundColor) {
  return ttmlText.replace(/<span([^>]*)>/gi, (match, attrs) => {
    let spanBg = defaultBackgroundColor;
    let newAttrs = attrs.replace(/\sstyle\s*=\s*(['"])(.*?)\1/gi, (styleMatch, quote, styleVal) => {
      const bgRule = styleVal.split(";").map(rule => rule.trim()).find(rule => rule.toLowerCase().startsWith("background-color:"));
      if (bgRule) {
        spanBg = bgRule.slice(bgRule.indexOf(":") + 1).trim();
      }
      const cleaned = styleVal.split(";").map(rule => rule.trim()).filter(rule => rule && !rule.toLowerCase().startsWith("background-color:")).join("; ");
      return cleaned ? ` style=${quote}${cleaned}${quote}` : "";
    });
    return `<span tts:backgroundColor='${spanBg}'${newAttrs}>`;
  });
};
export default {
  decode: function (input, options) {
    let regions = {},
      styles = {};
    const xmlToJson = new XMLParser({
      stopNodes: ["*.p"],
      numberParseOptions: {
        skipLike: /[0-9]+/
      },
      ignoreAttributes: false
    });
    let fileJson = xmlToJson.parse(input);
    if (!fileJson.tt) {
      fileJson = {
        tt: fileJson
      };
    }
    let timeBase = ttmlFunc.getTimeBaseValue(input) || "media";
    let useClockTime = timeBase === "media" || timeBase === "clock";
    let timing = getTimingInfo(input, options);
    let head = fileJson.tt.head || {};
    [].concat(head.layout?.region || []).forEach(region => {
      regions[region['@_xml:id']] = region;
    });
    [].concat(head.styling?.style || []).forEach(style => {
      styles[style['@_xml:id']] = style;
    });
    if (!fileJson.tt.body || !fileJson.tt.body.div) {
      return [];
    }
    let paragraphs = getParagraphs(fileJson);
    let hasExtents = paragraphs.some(({
      paragraph
    }) => paragraph["@_tts:extent"]);
    return hasExtents ? decodeWithExtents(paragraphs, regions, timing, useClockTime, options) : decodePremiere(paragraphs, styles, timing, useClockTime, options);
  },
  encode: function (eventGroup, options) {
    let encodingOptions = getFormatOptions(options.formatOptions);
    let languageCode = encodingOptions["Language Code"] || "en";
    let fontFamily = encodingOptions["Font Family"] || "monospace";
    let fontSize = encodingOptions["Font Size"] || 80;
    let fontWeight = encodingOptions["Font Weight"] || "normal";
    let fontColor = encodingOptions["Font Color"] || "white";
    let backgroundColor = encodingOptions["Background Color"] || "black";
    let tcFormat = encodingOptions["Timecode Format"] || "smpte";
    let timeBase = encodingOptions["Time Base"] || "media";
    let frameRateMultiplier = ttmlFunc.frameRateMultiplierMap[options.frameRate];
    let output = eol.after(`<?xml version="1.0" encoding="UTF-8"?>`);
    output += eol.after(`<tt xml:lang="${languageCode}"
        xmlns="http://www.w3.org/ns/ttml"
        xmlns:tts="http://www.w3.org/ns/ttml#styling"
        xmlns:ttm="http://www.w3.org/ns/ttml#metadata"
        xmlns:smpte="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt"
        xmlns:m608="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt#cea608"
        xmlns:ttp="http://www.w3.org/ns/ttml#parameter"
        ttp:dropMode="${options.dropFrame ? "dropNTSC" : 'nonDrop'}"
        ttp:timeBase="${timeBase}" ttp:frameRate="${ttmlFunc.frameRateMap[options.frameRate]}" ttp:frameRateMultiplier="${frameRateMultiplier}">`);
    output += eol.after(`<head>`);
    output += eol.after(`<metadata>`);
    output += eol.after(`<ttm:desc>${getAppName()}</ttm:desc>`);
    output += eol.after(`<smpte:information
        xmlns:m608="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt#cea608"
        origin="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt#cea608"
        mode="Preserved" m608:channel="CC1" m608:programName="Demo" m608:captionService="F1C1CC"
       />`);
    output += eol.after(`</metadata>`);
    output += eol.after(`<styling>
        <style xml:id='basic' tts:color='${fontColor}' tts:backgroundColor='${backgroundColor}' tts:fontFamily='${fontFamily}' tts:fontSize='${fontSize}%' tts:fontWeight="${fontWeight}"/>
       </styling>`);
    output += eol.after(`<layout>
        <region xml:id='pop1' tts:backgroundColor='transparent' tts:showBackground='whenActive'></region>
        <region xml:id='pop2' tts:backgroundColor='transparent' tts:showBackground='whenActive'></region>
        <region xml:id='pop3' tts:backgroundColor='transparent' tts:showBackground='whenActive'></region>
        <region xml:id='pop4' tts:backgroundColor='transparent' tts:showBackground='whenActive'></region>
        <region xml:id='paint' tts:backgroundColor='transparent' tts:showBackground='whenActive'></region>
        <region xml:id='paint2' tts:backgroundColor='transparent' tts:showBackground='whenActive'></region>
        <region xml:id='paint3' tts:backgroundColor='transparent' tts:showBackground='whenActive'></region>
        <region xml:id='paint4' tts:backgroundColor='transparent' tts:showBackground='whenActive'></region>
        <region xml:id='rollup2' tts:backgroundColor='transparent' tts:showBackground='whenActive'></region>
        <region xml:id='rollup3' tts:backgroundColor='transparent' tts:showBackground='whenActive'></region>
        <region xml:id='rollup4' tts:backgroundColor='transparent' tts:showBackground='whenActive'></region>
       </layout>`);
    output += eol.after(`</head>`);
    output += eol.after(`<body>`);
    output += eol.after(`<div>`);
    eventGroup.events.forEach(event => {
      // console.log(event);
      // console.log("-----------");
      // console.log(event.text);
      // console.log(event.xPos);
      // console.log(event.yPos);
      // console.log(event.alignment);

      let start, end;
      if (tcFormat.toLowerCase() === "smpte") {
        start = timeBase == "smpte" ? tcLib.secToTc(event.start, options.frameRate, options.dropFrame) : tcLib.secToTcClock(event.start, options.frameRate, options.dropFrame);
        end = timeBase == "smpte" ? tcLib.secToTc(event.end, options.frameRate, options.dropFrame) : tcLib.secToTcClock(event.end, options.frameRate, options.dropFrame);
      } else {
        start = tcLib.secToTcMs(event.start, options.frameRate, options.dropFrame).replace(",", ".");
        end = tcLib.secToTcMs(event.end, options.frameRate, options.dropFrame).replace(",", ".");
      }
      let plainTextCustom = convertToPlainTextCustom(event.text, "\n", false, "strong", "em", "u", true);
      eol.split(plainTextCustom).forEach((textLine, index, textLines) => {
        let ttmlText = ttmlFunc.singleLine.convertToTtml(textLine);
        // console.log(ttmlText);
        //If the ttmlText has spans (for fontStyle) we need to also add the tts:background color to the span to preserve the background color of the text when fontStyle is applied. This is because in TTML, when a style with a fontStyle is applied to text, it can sometimes override the background color of the region, so we need to explicitly set the background color on the span to ensure it is preserved.
        let ttmlTextProcessed = applySpanBackgroundColor(ttmlText, backgroundColor);
        let region = getRegion(index, event.style);
        let extents = ttmlFunc.singleLine.calcExtents(textLine, options.window, parseFloat(fontSize));
        let origins = ttmlFunc.singleLine.calcOrigin(event, textLine, index, textLines.length, parseFloat(fontSize), options.window);
        output += `    <p region='${region}' style='basic' begin='${start.replace(",", ".")}' end='${end.replace(",", ".")}' tts:origin='${origins}' tts:extent='${extents}'>${ttmlTextProcessed}</p>
`;
      });
    });
    output += eol.after(`</div>`);
    output += eol.after(`</body>`);
    output += eol.after(`</tt>`);
    return output;
  },
  preProcess: {
    encode: function (eventGroup) {
      return removeInvalidEvents(eventGroup);
    },
    decode: function (input) {
      return eol.lf(input.trim());
    }
  },
  postProcess: {
    encode: function (output) {
      return output;
    },
    decode: function (eventGroup) {
      return eventGroup;
    }
  }
};